import ReplayIcon from '@mui/icons-material/Replay';
import { Button, Stack } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { memo, useCallback, useMemo } from 'react';
import { useDataProvider } from 'react-admin';
import { useTranslation } from 'react-i18next';
import { LogSearchModal } from 'components/generic/LogViewer/LogSearchModal';
import {
  LogViewerLabels,
  LogViewerRow,
  LogViewerStatus,
} from 'components/generic/LogViewer/LogViewer';
import ErrorAlerts from 'components/generic/ErrorAlerts';
import { CoreLogEntry, CoreLogLevel } from 'providers/contexts/extensions/CoreLogs';
import {
  BroadcastMessageItem,
  GetBroadcastMessageIdResult,
} from 'providers/contexts/extensions/Broadcast';
import { ApiErrorResponse, BloxCustomDataProviderType } from 'utility/types/dataProvider';
import SkeletonBox from 'components/generic/SkeletonBox';
import { LogFilters } from 'components/generic/LogViewer/LogSearchProvider';

// UTC handling
dayjs.extend(utc);

/**
 * Notifier delivery logs
 */
const NOTIFIER_LOG_NAME = 'tncms_notifier';

// Days padded on each side of the send date for the initial window.
const NOTIFIER_LOG_WINDOW_DAYS = 3;

// Initial level selection, matching the legacy logsysSearch call (no debug).
const NOTIFIER_LOG_LEVELS: CoreLogLevel[] = [
  'info',
  'notice',
  'warning',
  'error',
  'fatal',
];

/** Pick the first non-empty value among candidate field keys. */
function pickField(fields: CoreLogEntry['fields'], keys: string[]): string {
  if (!fields) {
    return '';
  }
  for (const key of keys) {
    const value = fields[key];
    if (value !== undefined && value !== null && `${value}`.trim() !== '') {
      return `${value}`;
    }
  }
  return '';
}

/**
 * Resolve a row status from the per-target `delivery_status` when present,
 * otherwise from the entry level.
 */
function resolveStatus(entry: CoreLogEntry): LogViewerStatus {
  const deliveryStatus = pickField(entry?.fields, [
    'delivery_status',
    'status',
  ]).toLowerCase();

  if (deliveryStatus === 'success' || deliveryStatus === 'failed') {
    return deliveryStatus;
  }
  const level = (entry?.level || '').toLowerCase();
  if (level === 'error' || level === 'fatal') {
    return 'failed';
  }
  if (level === 'warning') {
    return 'warning';
  }
  if (level === 'info' || level === 'notice' || level === 'debug') {
    return 'info';
  }
  return 'unknown';
}

/** Map a raw notifier log entry to a generic LogViewer row. */
function toLogRow(entry: CoreLogEntry, index: number): LogViewerRow {
  const driver = pickField(entry?.fields, [
    'driver',
    'target_name',
    'target',
    'platform',
    'channel_name',
    'channel',
    'name',
  ]);

  const message =
    entry?.message ||
    pickField(entry?.fields, ['error', 'error_message', 'reason', 'detail', 'details']);

  return {
    id: `${driver || 'log'}-${entry?.time ?? ''}-${index}`,
    driver: driver || '',
    status: resolveStatus(entry),
    time: entry?.time,
    message: message || '',
    item: entry,
  };
}

/** Chronological order (oldest first), like the legacy modal. */
function orderChronological(rows: LogViewerRow[]): LogViewerRow[] {
  return [...rows].sort((a, b) => `${a.time ?? ''}`.localeCompare(`${b.time ?? ''}`));
}

export type NotifierLogsModalProps = {
  open: boolean;
  onClose: () => void;
  messageId: string | null;
};

export const NotifierLogsModal = memo(function NotifierLogsModal({
  open,
  onClose,
  messageId,
}: NotifierLogsModalProps) {
  const { t } = useTranslation();
  const dataProvider = useDataProvider<BloxCustomDataProviderType>();
  const strRetry = t('blox.actions.retry');

  // Fetch the send to seed the initial log-date window on its send date.
  const {
    data: itemData,
    error: itemErr,
    isLoading: itemLoading,
    refetch: refetchItem,
  } = useQuery<
    GetBroadcastMessageIdResult,
    ApiErrorResponse,
    BroadcastMessageItem | undefined
  >({
    queryKey: ['getBroadcastMessageId', messageId],
    queryFn: async () => {
      if (!messageId) {
        throw new Error(t('blox.notifier.logs.error.missing_params'));
      }
      return dataProvider.getBroadcastMessageId({ path: { id: messageId } });
    },
    enabled: open && Boolean(messageId),
    select: (data) => data?.data,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    gcTime: 0, // keep until modal close
    staleTime: 0, // always refresh when modal open
  });

  const handleRefetch = useCallback(() => {
    refetchItem();
  }, [refetchItem]);

  // Initial `[start, end]` window (YYYY-MM-DD) padded around the send's UTC day,
  // seeded as a visible, editable date filter. Undefined until the send loads.
  const initialDateRange = useMemo<[string, string] | undefined>(() => {
    const sendDate = itemData?.send_date;
    if (!sendDate) {
      return undefined;
    }
    const sendDay = dayjs.utc(sendDate);
    return [
      sendDay.subtract(NOTIFIER_LOG_WINDOW_DAYS, 'day').format('YYYY-MM-DD'),
      sendDay.add(NOTIFIER_LOG_WINDOW_DAYS, 'day').format('YYYY-MM-DD'),
    ];
  }, [itemData?.send_date]);

  const initFilters: LogFilters = useMemo(() => {
    const filters: LogFilters = {
      level: NOTIFIER_LOG_LEVELS,
      ...(initialDateRange?.[0] ? { start_date: initialDateRange[0] } : {}),
      ...(initialDateRange?.[1] ? { end_date: initialDateRange[1] } : {}),
    };
    return filters;
  }, [initialDateRange]);

  const labels: LogViewerLabels = useMemo(
    () => ({
      columnTime: t('blox.notifier.logs.column.time'),
      columnStatus: t('blox.notifier.logs.column.status'),
      columnDriver: t('blox.notifier.logs.column.driver'),
      columnMessage: t('blox.notifier.logs.column.message'),
      statusSuccess: t('blox.notifier.logs.target_status.success'),
      statusFailed: t('blox.notifier.logs.target_status.failed'),
      statusWarning: t('blox.notifier.logs.target_status.warning'),
      statusInfo: t('blox.notifier.logs.target_status.info'),
      statusUnknown: t('blox.notifier.logs.target_status.unknown'),
      empty: t('blox.notifier.logs.no_results'),
      loadingMore: t('blox.notifier.logs.loading_more'),
    }),
    [t],
  );

  const loading = useMemo(() => {
    if (!itemLoading) {
      return undefined;
    }
    return (
      <SkeletonBox
        width="100%"
        height="100%"
      />
    );
  }, [itemLoading]);

  const error = useMemo(() => {
    if (!itemErr) {
      return undefined;
    }
    return (
      <ErrorAlerts
        severity="warning"
        buttonText={strRetry}
        buttonIcon={<ReplayIcon />}
        buttonOnClick={handleRefetch}
        showDetails={true}
        errors={[
          {
            status: `${itemErr?.status || itemErr?.axiosStatus}`,
            message: `${itemErr?.message || itemErr?.axiosMessage}`,
            fullDetails: itemErr,
          },
        ]}
      />
    );
  }, [handleRefetch, itemErr, strRetry]);

  const footer = useMemo(() => {
    if (itemErr) {
      return null;
    }
    return (
      <Stack
        direction="row"
        justifyContent="flex-end"
        sx={{ width: '100%' }}
        data-testid="notifier-logs-footer"
      >
        <Button
          data-testid="notifier-logs-close-button"
          variant="outlined"
          color="primary"
          size="medium"
          onClick={onClose}
        >
          {t('blox.actions.close')}
        </Button>
      </Stack>
    );
  }, [itemErr, onClose, t]);

  return (
    <LogSearchModal
      dataTestId="notifier-logs-modal"
      open={open}
      onClose={onClose}
      title={t('blox.notifier.logs.title')}
      log={NOTIFIER_LOG_NAME}
      fixedFields={{ message_id: messageId }}
      initialFilters={initFilters}
      toRow={toLogRow}
      transformRows={orderChronological}
      labels={labels}
      enabled={open && Boolean(messageId)}
      loading={loading}
      error={error}
      footer={footer}
    />
  );
});

NotifierLogsModal.displayName = 'NotifierLogsModal';
